"use client";

import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import ListingCard from "@/src/shared/components/listing/ListingCard";
import SeeAllCard from "@/src/shared/components/listing/SeeAllCard";
import { cn } from "@/src/lib/utils";
import { useEffect, useRef, useState } from "react";
import type { ICampaignListing } from "@/src/shared/types/listing";

interface IListingSliderProps {
  title: string;
  subtitle?: string | null;
  listings: ICampaignListing[];
  variant?: "default" | "dashboard";
  /** The rail's own page: the title links to it, with Airbnb's arrow after it. */
  href?: string;
  /** Called on a title click first; it may `preventDefault()` (mobile opens a drawer). */
  onOpen?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Extra details-link params per card (a boosted card's `boost_id` and `from_page`). */
  cardQuery?: (item: ICampaignListing) => Record<string, string> | undefined;
  /** Every card is paid-boosted (Featured Stays): each gets the "Sponsored" pill. */
  sponsored?: boolean;
  /**
   * The home page's rail, as Airbnb's: seven cards 12px apart from 1440px (six from
   * 1280px), and a "See all" card after the last stay that opens `href`.
   */
  homeRow?: boolean;
}

const DEFAULT_CARD =
  "w-[calc(50%-0.75rem)] sm:w-[calc(33.33%-1rem)] lg:w-[calc(20%-1.2rem)] xl:w-[calc(16.66%-1.25rem)]";
/** Airbnb's home rail: 2 / 3 cards 24px apart, then 5, 6 and 7 cards 12px apart. */
export const HOME_CARD =
  "w-[calc((100%-1.5rem)/2)] sm:w-[calc((100%-3rem)/3)] lg:w-[calc((100%-3rem)/5)] xl:w-[calc((100%-3.75rem)/6)] min-[1440px]:w-[calc((100%-4.5rem)/7)]";

/** The rail's title: plain, or Airbnb's link with a round arrow right after it. */
const RailTitle = ({
  title,
  href,
  onOpen,
}: Pick<IListingSliderProps, "title" | "href" | "onOpen">) => {
  const heading = (
    <h2 className="text-base sm:text-lg md:text-2xl font-semibold text-ink-black">
      {title}
    </h2>
  );
  if (!href) return heading;
  return (
    <Link
      href={href}
      onClick={onOpen}
      className="group inline-flex max-w-full items-center gap-2"
    >
      {heading}
      <span
        aria-hidden
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F2F2F2] text-ink transition-colors group-hover:bg-[#EBEBEB]"
      >
        <ArrowRight size={16} strokeWidth={2.25} />
      </span>
    </Link>
  );
};

const ListingSlider = ({
  title,
  subtitle,
  listings,
  variant = "default",
  href,
  onOpen,
  cardQuery,
  sponsored = false,
  homeRow = false,
}: IListingSliderProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollability = () => {
    const { current } = scrollRef;
    if (current) {
      const isAtStart = current.scrollLeft <= 1;
      const isAtEnd =
        current.scrollWidth - current.scrollLeft - current.clientWidth <= 1;
      setCanScrollLeft(!isAtStart);
      setCanScrollRight(!isAtEnd);
    }
  };

  useEffect(() => {
    const currentRef = scrollRef.current;
    if (!currentRef) return;
    checkScrollability();
    const handleScroll = () => checkScrollability();
    currentRef.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", checkScrollability);
    return () => {
      currentRef.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkScrollability);
    };
  }, [listings]);

  // dashboard variant: container-aware sizing with max 4 items
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [itemsPerView, setItemsPerView] = useState<number | null>(null);

  useEffect(() => {
    if (variant !== "dashboard") return;
    const el = wrapperRef.current;
    if (!el) return;

    const calculate = (w: number) => {
      if (w < 576) return 2;
      if (w < 900) return 4;
      if (w < 1200) return 5;
      return 5;
    };

    const apply = () => setItemsPerView(calculate(el.clientWidth));

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => apply());
      ro.observe(el);
    } else {
      apply();
      window.addEventListener("resize", apply);
    }

    apply();
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", apply);
    };
  }, [variant, listings]);

  const scroll = (direction: "left" | "right") => {
    const { current } = scrollRef;
    if (current) {
      const scrollAmount = current.clientWidth * 0.9;
      current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  if (variant === "dashboard") {
    return (
      <div ref={wrapperRef} className="mb-12">
        {title && (
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-base sm:text-lg md:text-2xl font-semibold text-ink-black">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-1 text-sm text-ink-gray">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => scroll("left")}
                aria-label="Scroll left"
                disabled={!canScrollLeft}
                className={`w-8 h-8 flex items-center justify-center border rounded-full ${canScrollLeft ? "bg-white hover:border-gray-800" : "opacity-40 bg-gray-100"}`}
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => scroll("right")}
                aria-label="Scroll right"
                disabled={!canScrollRight}
                className={`w-8 h-8 flex items-center justify-center border rounded-full ${canScrollRight ? "bg-white hover:border-gray-800" : "opacity-40 bg-gray-100"}`}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
        <div
          ref={scrollRef}
          className="flex gap-4 lg:gap-6 overflow-x-auto hide-scrollbar pb-2"
        >
          {listings.map((item, index) => {
            // No width until the container is measured (the browser used to drop the
            // invalid `Infinity%` this produced, so the first paint is unchanged).
            const width = itemsPerView
              ? `calc(${100 / itemsPerView}% - ${itemsPerView > 1 ? "1rem" : "0px"})`
              : undefined;
            return (
              <div
                key={`${item.pivot_id ?? item.id}-${index}`}
                style={
                  width ? { flex: `0 0 ${width}`, maxWidth: width } : undefined
                }
              >
                <ListingCard data={item.listing} />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Default (original) rendering with improved responsiveness
  return (
    <div className="mb-12">
      <div className="flex justify-between items-center mb-6">
        <div className="min-w-0">
          <RailTitle title={title} href={href} onOpen={onOpen} />
          {subtitle && <p className="mt-1 text-sm text-ink-gray">{subtitle}</p>}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => scroll("left")}
            aria-label="Scroll left"
            disabled={!canScrollLeft}
            className={`w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full transition-all ${
              canScrollLeft
                ? "hover:border-gray-800 hover:shadow-md"
                : "opacity-40 cursor-not-allowed bg-[#C3C6D3]"
            }`}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => scroll("right")}
            aria-label="Scroll right"
            disabled={!canScrollRight}
            className={`w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full transition-all ${
              canScrollRight
                ? "hover:border-gray-800 hover:shadow-md"
                : "opacity-40 cursor-not-allowed bg-[#C3C6D3]"
            }`}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className={cn(
          "flex overflow-x-auto hide-scrollbar",
          homeRow ? "gap-6 lg:gap-3" : "space-x-6",
        )}
      >
        {listings.map((item, index) => (
          <div
            key={`${item.pivot_id ?? item.id}-${index}`}
            className={cn("flex-shrink-0", homeRow ? HOME_CARD : DEFAULT_CARD)}
          >
            <ListingCard
              data={item.listing}
              linkQuery={cardQuery?.(item)}
              isSponsored={sponsored}
            />
          </div>
        ))}
        {homeRow && href && (
          <div className={cn("flex-shrink-0", HOME_CARD)}>
            <SeeAllCard
              href={href}
              onOpen={onOpen}
              photos={listings
                .slice(0, 3)
                .map((item) => item.listing.images?.[0]?.url)
                .filter((url): url is string => !!url)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingSlider;
